import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import { motion } from 'framer-motion';
import { auth, db } from '../firebaseConfig';
import { signInWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

const AdminLogin = ({ setIsAdminLoggedIn }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true); // New state for initial auth check
  const navigate = useNavigate();

  // Check if user is already logged in
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // User is logged in, check if they are admin
        const userRef = doc(db, 'users', user.email);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists() && userSnap.data().role === 'admin') {
          localStorage.setItem('email', user.email);
          setIsAdminLoggedIn(true);
          navigate('/admin/dashboard');
        } else {
          // Not an admin, sign out
          await auth.signOut();
          localStorage.removeItem('email');
          setIsAdminLoggedIn(false);
        }
      }
      // Done checking auth, show login form if no user or not admin
      setIsCheckingAuth(false);
    });

    return () => unsubscribe(); // Cleanup subscription on unmount
  }, [navigate, setIsAdminLoggedIn]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const userRef = doc(db, 'users', user.email);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists() && userSnap.data().role === 'admin') {
        localStorage.setItem('email', user.email);
        setIsAdminLoggedIn(true);
        navigate('/admin/dashboard');
      } else {
        setError('You do not have admin privileges.');
        await auth.signOut();
      }
    } catch (err) {
      setError('Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading bar while checking auth
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="w-64">
          <div className="bg-[#DBAE6E]/20 h-2 rounded-full overflow-hidden">
            <motion.div
              className="bg-[#DBAE6E] h-full"
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ duration: 1.5, ease: 'easeInOut', repeat: Infinity, repeatType: 'reverse' }}
            />
          </div>
          <p className="text-[#DBAE6E] text-center mt-4">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Show login form if not checking auth
  return (
    <div className="p-8 max-w-4xl mx-auto bg-black text-[#DBAE6E] shadow-xl rounded-2xl mt-20">
      <h2 className="text-4xl font-bold mb-10 text-center">Admin Login</h2>

      <form onSubmit={handleLogin} className="space-y-8 max-w-md mx-auto">
        {/* Email */}
        <div>
          <label className="block font-semibold text-lg mb-2">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 border border-[#DBAE6E] bg-black text-[#DBAE6E] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#DBAE6E] transition-all"
            placeholder="Enter your email"
            required
            disabled={isLoading}
          />
        </div>

        {/* Password */}
        <div className="relative">
          <label className="block font-semibold text-lg mb-2">Password</label>
          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 border border-[#DBAE6E] bg-black text-[#DBAE6E] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#DBAE6E] transition-all pr-12"
            placeholder="Enter your password"
            required
            disabled={isLoading}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-12 text-[#DBAE6E] hover:text-white transition-colors"
            disabled={isLoading}
          >
            {showPassword ? <EyeSlashIcon className="h-6 w-6" /> : <EyeIcon className="h-6 w-6" />}
          </button>
        </div>

        {/* Error */}
        {error && (
          <p className="text-red-500 text-sm text-center font-medium bg-red-100/10 p-2 rounded-md">
            {error}
          </p>
        )}

        {/* Login Button */}
        <motion.button
          type="submit"
          className={`w-full py-4 px-8 text-xl font-semibold rounded-lg transition-all ${
            isLoading
              ? 'bg-[#DBAE6E]/40 text-black cursor-not-allowed'
              : 'bg-gradient-to-r from-[#DBAE6E] to-[#DBAE6E] text-black hover:from-[#bfa258] hover:to-[#cbb074]'
          }`}
          whileTap={!isLoading ? { scale: 0.97 } : {}}
          disabled={isLoading}
        >
          {isLoading ? 'Logging in...' : 'Login'}
        </motion.button>
      </form>
    </div>
  );
};

export default AdminLogin;